import React from "react";
import { motion } from "framer-motion";
import { Wrench, CalendarClock, CalendarCheck, MessageCircle, Info, Clock, Truck, Siren } from "lucide-react";

const WA = "https://wa.me/34617420491?text=" + encodeURIComponent(
  "Hola Moisés, he visto el tarifario 2026 y me gustaría más información / un presupuesto."
);

const GROUPS = [
  {
    icon: Wrench,
    title: "Mano de obra y desplazamiento",
    accent: "copper",
    items: [
      { icon: Clock, label: "Hora de mano de obra estándar", price: "30,00 €", unit: "/ hora" },
      { icon: Truck, label: "Tasa de desplazamiento", price: "30,00 €", unit: "" },
      {
        icon: Siren,
        label: "Servicio de urgencia 24h",
        price: "90,00 €",
        unit: "",
        note: "Incluye desplazamiento y la 1ª hora de intervención",
      },
    ],
  },
  {
    icon: CalendarClock,
    title: "Abonos mensuales",
    subtitle: "Mantenimiento preventivo",
    accent: "cyan",
    items: [
      { label: "Bloques de viviendas / Comunidades", price: "Desde 90,00 €", unit: "/ mes" },
      { label: "Locales comerciales / Negocios", price: "Desde 120,00 €", unit: "/ mes" },
    ],
    footer: "Incluye revisiones periódicas de cuadros, iluminación y comprobación de diferenciales y encendidos.",
  },
  {
    icon: CalendarCheck,
    title: "Planes anuales puntuales",
    accent: "copper",
    items: [
      { label: "Preventivo residencial (vivienda)", price: "85,00 €", unit: "/ año" },
      { label: "Preventivo comunidades", price: "250,00 €", unit: "/ año" },
      { label: "Preventivo comercial completo", price: "250,00 €", unit: "/ año" },
    ],
  },
];

const CONDITIONS = [
  "Los precios expuestos NO incluyen el 7% de IGIC aplicable.",
  "Las averías detectadas se presupuestarán por separado.",
  "Los materiales empleados se facturarán aparte.",
];

export default function Pricing() {
  return (
    <section id="tarifario" className="relative py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <span className="section-label mb-4">04 · Tarifario 2026</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mt-4 max-w-3xl">
              Precios claros, <span className="text-[#E57C23]">sin letra pequeña.</span>
            </h2>
          </div>
          <p className="text-[#9CA3AF] max-w-md text-[15px] leading-relaxed">
            Estas son mis tarifas de referencia para 2026. Todo por escrito y por
            adelantado — así sabes exactamente qué vas a pagar antes de que
            empiece el trabajo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {GROUPS.map((g, gi) => {
            const Icon = g.icon;
            const isCopper = g.accent === "copper";
            return (
              <motion.div
                key={g.title}
                data-testid={`pricing-group-${gi}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: gi * 0.08 }}
                className="card-circuit p-7 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="h-11 w-11 rounded-lg border border-white/10 bg-[#0A0D14] flex items-center justify-center"
                    style={{ color: isCopper ? "#E57C23" : "#00E5FF" }}
                  >
                    <Icon size={20} strokeWidth={1.9} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-white leading-tight">{g.title}</h3>
                    {g.subtitle && (
                      <div className="font-mono text-[11px] text-[#00E5FF]/70 tracking-wide mt-0.5">
                        {g.subtitle}
                      </div>
                    )}
                  </div>
                </div>

                <ul className="space-y-4 flex-1">
                  {g.items.map((it, ii) => {
                    const ItIcon = it.icon;
                    return (
                      <li key={ii} className="border-b border-white/6 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-2.5">
                            {ItIcon && (
                              <ItIcon size={15} className="text-white/40 mt-1 shrink-0" />
                            )}
                            <span className="text-[14.5px] text-white/85 leading-snug">{it.label}</span>
                          </div>
                          <div className="text-right shrink-0">
                            <span
                              className="font-display text-lg whitespace-nowrap"
                              style={{ color: isCopper ? "#FF8A00" : "#33efff" }}
                            >
                              {it.price}
                            </span>
                            {it.unit && (
                              <span className="text-white/40 text-[12px] ml-1">{it.unit}</span>
                            )}
                          </div>
                        </div>
                        {it.note && (
                          <p className="mt-1.5 pl-6 text-[12px] text-white/45 leading-snug">{it.note}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {g.footer && (
                  <p className="mt-5 pt-4 border-t border-white/8 text-[12px] text-white/50 leading-snug flex gap-2">
                    <Info size={13} className="mt-0.5 shrink-0 text-[#00E5FF]/70" />
                    {g.footer}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
          <div className="lg:col-span-2 rounded-2xl border border-white/8 bg-[#121620]/60 p-6">
            <div className="font-mono text-[11px] text-white/40 tracking-widest mb-3">
              CONDICIONES COMERCIALES
            </div>
            <ul className="grid sm:grid-cols-3 gap-3">
              {CONDITIONS.map((c, i) => (
                <li
                  key={i}
                  className="text-[13px] text-white/70 leading-snug border-l-2 border-[#E57C23]/50 pl-3"
                >
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-[#E57C23]/30 bg-gradient-to-br from-[#171c28] to-[#0d111a] p-6 flex flex-col justify-between">
            <div>
              <div className="font-display text-lg text-white">¿Presupuesto a medida?</div>
              <p className="text-[13px] text-white/60 mt-2 leading-snug">
                Cada instalación es distinta. Te confirmo el precio final sin
                compromiso tras una revisión rápida.
              </p>
            </div>
            <a
              href={WA}
              target="_blank"
              rel="noreferrer"
              data-testid="pricing-whatsapp-btn"
              className="btn-copper mt-5 justify-center"
            >
              <MessageCircle size={16} strokeWidth={2.4} />
              Pedir presupuesto
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
